import {Box, BoxProps, Divider, LinearProgress, ListItemIcon, MenuItem, ModalProps, styled, Typography} from "@mui/material";
import React, {FC, useEffect, useState, isValidElement, cloneElement, ReactNode, useContext, useRef} from "react";
import {HistoricNotificationDrawer, NavBar, PageBaseProfile, SideMenu} from "@buildwitt/shapeshift-common/v1/pageBase";
import {makeStyles} from "tss-react/mui";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useLocation, useNavigate} from "react-router-dom";
import {customerSupportLink, mobileNavBarHeight, palette, sideMenuWidth, suggestFeatureLink, toolbarHeight} from "@/constants";
import {IconMyProgress} from "@/icons/IconMyProgress";
import {MobileNavBar} from "./mobileNavBar";
import {ProfileImage} from "./profileImage";
import {PageBaseContext} from "@/contexts/pageBaseContext";
const logo = "/logo.svg"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {Footer} from "./footer";
import {StatusBar, Style} from "@capacitor/status-bar";
import {IconNotes} from "@/icons/IconNotes";
import {Capacitor} from "@capacitor/core";
import {IconPlayButton} from "@/icons/IconPlayButton";
import {IconCourseVideos} from "@/icons/IconCourseVideos";
import {IconFolder} from "@/icons/IconFolder";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {Browser} from "@capacitor/browser";
import PersonIcon from "@mui/icons-material/Person";
import BugReportOutlined from "@mui/icons-material/BugReportOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import CodeIcon from "@mui/icons-material/Code";
import TranslateIcon from "@mui/icons-material/Translate";
import useDevCycleSdk from "../../hooks/useDevCycleSdk";
import CustomTooltip from "../common/CustomTooltip";

interface ICustomProps {
	children: React.ReactNode,
	contentWidth?: string,
	sideMenuWidthOverride?: string,
	sideBarContent?: ReactNode,
	disableSideBar?: boolean,
	disableFooter?: boolean
}

interface IStyleProps {
	sideMenuOpen: boolean,
	hasNavBar: boolean,
	contentWidth?: string,
	sideMenuWidthOverride?: string,
}

const useStyles = makeStyles<IStyleProps>()((theme, {sideMenuOpen, hasNavBar, contentWidth, sideMenuWidthOverride}) => ({
	pageBase: {
		height: "100%"
	},
	notificationsDrawer: {
		"[class*='notificationDrawerContainer']": {
			backgroundColor: "#191919",
			"[class*='historicNotificationItem']": {
				backgroundColor: "#383838"
			},
			[theme.breakpoints.down("md")]: {
				width: "100%",
				maxWidth: "100%"
			},
			"[class*='markAllAsReadMobile']": {
				position: "absolute",
				top: "55px",
				textAlign: "left",
				color: palette.orange
			}
		}
	},
	pageBaseProfile: {
		alignItems: "center"
	},
	background: {
		backgroundColor: theme.palette.background.default,
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: "-1",
		position: "fixed"
	},
	navBar: {
		borderBottom: palette.midGrey3 + " 1px solid",
		background: palette.black,
		fontWeight: 600,
		paddingTop: "env(safe-area-inset-top)",
		"[class*='toolBar']": {
			height: theme.toolbarHeight
		},
		".NavBarTabs": {
			[theme.breakpoints.down("lg")]: {
				display: "none"
			}
		},
		"[class*='logoContainer']": {
			"& img": {
				display: "block",
				transition: "all .3s ease-in-out"
			},
			"& [class*='navBarLogo']": {
				marginRight: "20px"
			}
		},
		"svg path": {
			fill: theme.palette.common.white
		},
		"[class*='menuIconContainer']": {
			[theme.breakpoints.down("lg")]: {
				display: "none"
			}
		},
		"[class*='bellIconContainer']": {
			background: "none",
			height: toolbarHeight,
			width: "40px",
			margin: 0,
			marginTop: "-1px",
			marginRight: "5px",
			borderRadius: 0,
			"svg": {
				position: "relative",
				right: "-3px",
				top: "0px"
			}
		}
	},
	usernameLabelContainer: {
		cursor: "pointer",
		gap: "40px",
		textAlign: "right",
		[theme.breakpoints.down("lg")]: {
			padding: "0 10px 0 0",
			gap: "10px"
		},
		display: "flex",
		justifyContent: "space-between",
		"& svg": {
			marginTop: "6px",
			marginRight: "-8px",
			"path": {
				fill: palette.grey60
			}
		}
	},
	usernameLabel: {
		color: palette.grey60,
		fontWeight: 600,
		fontSize: "15px"
	},
	navBarSpacer: {
		width: "100%",
		height: toolbarHeight
	},
	contentContainer: {
		width: sideMenuOpen ? `calc(100% - ${sideMenuWidthOverride || sideMenuWidth})` : "100%",
		position: "absolute",
		overflowX: "hidden",
		transition: "all .3s ease-in-out",
		left: sideMenuOpen ? (sideMenuWidthOverride || sideMenuWidth) : "0px",
		boxSizing: "border-box",
		overflowY: "auto",
		top: hasNavBar ? `calc(${toolbarHeight} + env(safe-area-inset-top))` : "env(safe-area-inset-top)",
		height: hasNavBar ? `calc(100% - ${toolbarHeight} - env(safe-area-inset-top))` : "calc(100% - env(safe-area-inset-top)",
		[theme.breakpoints.down("lg")]: {
			height: `calc(100% - ${mobileNavBarHeight} - ${toolbarHeight} - env(safe-area-inset-top))`
		}
	},
	sideMenu: {
		position: "fixed",
		"& [class*='moduleTitle']": {
			color: "white"
		}
	},
	content: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		height: "auto",
		minHeight: "100%",
		boxSizing: "border-box",
		width: "100%",
		maxWidth: contentWidth ?? "1000px",
		margin: "auto"
	},
	contentInner: {
		display: "flex",
		flexGrow: 1,
		flexDirection: "column",
		minHeight: "100%",
		marginBottom: "-10px",
		position: "relative",
		zIndex: "1"
	},
	navIcon: {
		marginTop: "3px",
		maxWidth: "20px",
		display: "inline-block"
	},
	loginBtnContainer: {
		display: "flex",
		gap: "10px",
		"& button": {
			padding: "10.7px 22.9px 10.6px 22.9px",
			width: "123px",
			background: theme.palette.common.white,
			color: theme.palette.primary.main,
			fontSize: "12px",
			"&:hover": {
				background: theme.palette.common.white
			}
		}
	},
	profileAvatar: {
		width: "40px",
		height: "40px",
		padding: "12px 8px 12px 12px",
		background: palette.black,
		margin: 0,
		"& img": {
			objectFit: "cover",
			borderRadius: "100%",
			overflow: "hidden"
		}
	},
	icon: {
		color: theme.palette.action.active
	},
	divider: {
		marginLeft: "1rem",
		marginRight: "1rem"
	},
	organization: {
		margin: "0.5rem 1rem 0.5rem 1rem",
		color: theme.palette.text.disabled,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		textTransform: "uppercase",
		maxWidth: "250px",
		letterSpacing: "2px",
		fontSize: "11px"
	},
	menuItem: {
		fontFamily: theme.typography.fontFamily
	}
}));

type IProps = ICustomProps & Omit<BoxProps, "children">;
type pages = "My Feed" | "Course Library" | "My Progress" | "Content Manager" | "My Notes";

type routeCallback = {
	[key in pages]: string;
};

export const PageBase: FC<IProps> = ({sideBarContent, disableSideBar, disableFooter, contentWidth, children, className, sideMenuWidthOverride, ...props}) => {
	const mq = useMq();
	const location = useLocation();
	const [, setContextState] = useContext(PageBaseContext)
	const [sideMenuOpen, setSideMenuOpen] = useState(!disableSideBar && mq.desktop);
	const {classes, cx} = useStyles({sideMenuOpen, hasNavBar: true, contentWidth, sideMenuWidthOverride});
	const navigate = useNavigate();
	const [tabs, setTabs] = useState<pages[]>([]);
	const [pageBaseContext, setPageBaseContext] = useContext(PageBaseContext);
	const [platform] = useState(Capacitor.getPlatform());
	const contentContainer = useRef<HTMLElement>(null);
	const [firstRender, setFirstRender] = useState(true);
	const {t} = useSSTranslation();
	const {topMenuUpdate, newHomeScreen} = useDevCycleSdk();

	const notificationTranslations = {
		title: t("notifications_title", "Notifications"),
		markAllAsRead: t("notifications_mark_all_read", "Mark all as read"),
		clearAll: t("notifications_clear_all", "Clear all"),
		allUpToDate: t("notifications_all_up_to_date", "You are all up to date!"),
		markedAllRead: t("notifications_marked_all_read", "Marked all notifications as read"),
		closeDrawer: t("notifications_close_drawer", "Close notifications drawer")
	};

	const labelTranslations: Record<pages, string> = {
		"My Feed": newHomeScreen ? t("page_home", "Home") : t("nav_feed", "My Feed"),
		"Course Library": t("nav_library", "Course Library"),
		"My Progress": t("nav_progress", "My Progress"),
		"My Notes": t("nav_notes", "My Notes"),
		"Content Manager": t("nav_manager", "Content Manager")
	}

	const routesMap: routeCallback = {
		"My Feed": newHomeScreen ? "/home" : "/training-feed",
		"Course Library": "/course-library",
		"My Progress": "/my-progress",
		"My Notes": "/notes",
		"Content Manager": "/admin"
	}

	const slimLabels: string[] = [
		newHomeScreen ? t("page_home", "Home") : t("nav_mobile_feed", "Feed"),
		t("nav_mobile_library", "Library"),
		t("nav_mobile_progress", "Progress"),
		t("nav_mobile_notes", "Notes")
	]

	useEffect(() => setSideMenuOpen(!disableSideBar && mq.desktop), [disableSideBar, mq.desktop]);

	useEffect(() => setFirstRender(false), [])

	useEffect(() => {
		if (!firstRender) {
			setContextState(prev => ({...prev, contentContainerRef: contentContainer}))
		}
	}, [firstRender])

	//for phone chins
	useEffect(() => {
		if (platform === "web" || platform === "android")
			return;

		// Make the status bar overlay the webview
		StatusBar.setOverlaysWebView({overlay: true});
		StatusBar.setStyle({style: Style.Dark});
	}, []);

	let pageSelected: pages | undefined = undefined;
	Object.keys(routesMap).forEach(e => {
		const page = e as pages;
		if (location.pathname.startsWith(routesMap[page]))
			pageSelected = page;
	})
	const handlePageSelect = (page: pages): void => {
		navigate(routesMap[page]);
	}

	const handleContentClick = (): void => {
		if (!mq.desktop && sideMenuOpen) setSideMenuOpen(false);
	}

	const tabIcons = [
		<IconPlayButton key={"icon_" + 1} className={classes.navIcon} />,
		<IconCourseVideos key={"icon_" + 2} className={classes.navIcon} />,
		<IconMyProgress key={"icon_" + 3} className={classes.navIcon} />,
		<IconNotes key={"icon_" + 4} className={classes.navIcon} />,
		<IconFolder key={"icon_" + 5} className={classes.navIcon} />
	]

	useEffect(() => {
		const tabs: pages[] = ["My Feed", "Course Library", "My Progress", "My Notes"];
		if (pageBaseContext.ownedCohort) {
			tabs.push("Content Manager");
		}

		setTabs(tabs);
	}, [pageBaseContext.ownedCohort])



	const RedirectToAccountPage = ({open, onClose}: Pick<ModalProps, "open" | "onClose">): JSX.Element => {
		if (open) {
			Browser.open({url: process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? ""});
			onClose?.("backdropClick", "escapeKeyDown");
		}
		return <LinearProgress />;
	};

	return (
		<Box className={cx(classes.pageBase, className)} {...props}>
			<NavBar<pages>
				labelOverride={(page) => labelTranslations[page]}
				className={classes.navBar}
				tabs={tabs}
				icons={tabIcons}
				onBurgerAction={() => setSideMenuOpen(prev => !prev)}
				onPageSelect={handlePageSelect}
				pageSelected={pageSelected}
				onLogoClick={() => navigate("/")}
				logoPath={logo}
				disableSideBar={disableSideBar}
				componentOverrides={{
					pageBaseProfile: (props) => <PageBaseProfile {...props}
						enableNotifications
						className={classes.pageBaseProfile}
						onNotificationAction={() => setPageBaseContext(prev => ({...prev, notificationDrawerOpen: !prev.notificationDrawerOpen}))}
						data={{notificationCount: pageBaseContext.notificationCount}}
						componentOverrides={{
							profileAvatar: ({children, className, ...p}) => <ProfileImage {...p} className={cx(className, classes.profileAvatar)} />,
							editAccountModal: ({open, onClose}) => <RedirectToAccountPage open={open} onClose={onClose} />,
							usernameLabel: ({className, onClick, ...p}) => <Box onClick={onClick} className={classes.usernameLabelContainer}>
								<>
									<KeyboardArrowDownIcon />
								</>
							</Box>,
							menu: ({logoutAction, accountSettingsAction}) => topMenuUpdate ? [
								<CustomTooltip
									key={"companyNameTooltip"}
									title={pageBaseContext.organizationName}
								>
									<Typography
										key={"companyNameLabel"}
										className={classes.organization}
									>
										{pageBaseContext.organizationName}
									</Typography>
								</CustomTooltip>,
								<CustomMenuItem
									key={"pagebase-setting-btn"}
									data-test-id="pagebase-setting-btn"
									onClick={() => navigate("/settings")}
								>
									<ListItemIcon className={classes.icon}>
										<TranslateIcon fontSize="small" />
									</ListItemIcon>
									{t("menu_profile_language_settings", "Language Preferences")}
								</CustomMenuItem>,
								<Divider key={"first-divider"} className={classes.divider} />,
								<CustomMenuItem
									key={"pagebase-account-setting-btn"}
									data-test-id="pagebase-account-setting-btn"
									onClick={() => accountSettingsAction()}
								>
									<ListItemIcon className={classes.icon}>
										<PersonIcon fontSize="small" />
									</ListItemIcon>
									{t("menu_profile_profile", "Profile")}
								</CustomMenuItem>,
								<CustomMenuItem
									key={"pagebase-report-issue-btn"}
									data-test-id="pagebase-report-issue-btn"
									onClick={() => window.open(customerSupportLink, "_blank", "noopener,noreferrer")}
								>
									<ListItemIcon className={classes.icon}>
										<BugReportOutlined fontSize="small" />
									</ListItemIcon>
									{t("menu_profile_report_issue", "Report issue")}
								</CustomMenuItem>,
								<CustomMenuItem
									key={"pagebase-suggest_feature-btn"}
									data-test-id="pagebase-suggest_feature-btn"
									onClick={() => window.open(suggestFeatureLink, "_blank", "noopener,noreferrer")}
								>
									<ListItemIcon className={classes.icon}>
										<CodeIcon fontSize="small" />
									</ListItemIcon>
									{t("menu_profile_suggest_feature", "Suggest a feature")}
								</CustomMenuItem>,
								<Divider key={"second-divider"} className={classes.divider} />,
								<CustomMenuItem
									key={"pagebase-profile-logout-btn"}
									data-test-id="pagebase-profile-logout-btn"
									onClick={logoutAction}
								>
									<ListItemIcon className={classes.icon}>
										<LogoutIcon fontSize="small" />
									</ListItemIcon>
									{t("menu_profile_logout", "Log Out")}
								</CustomMenuItem>
							] : [
								<MenuItem
									key={"pagebase-setting-btn"}
									data-test-id="pagebase-setting-btn"
									onClick={() => navigate("/settings")}
								>
									{t("menu_profile_settings", "Training Preferences")}
								</MenuItem>,
								<MenuItem
									key={"pagebase-account-setting-btn"}
									data-test-id="pagebase-account-setting-btn"
									onClick={() => accountSettingsAction()}
								>
									{t("menu_profile_account", "My Account")}
								</MenuItem>,
								<MenuItem
									key={"pagebase-support-btn"}
									data-test-id="pagebase-support-btn"
									onClick={() => window.location.href = ("https://www.buildwitt.com/software/construction-training-software/support")}
								>
									{t("menu_profile_support", "Support")}
								</MenuItem>,
								<MenuItem
									key={"pagebase-profile-logout-btn"}
									data-test-id="pagebase-profile-logout-btn"
									onClick={logoutAction}
								>
									{t("menu_profile_logout", "Log Out")}
								</MenuItem>
							]
						}}
					/>
				}}
			/>
			<SideMenu
				className={classes.sideMenu}
				open={sideMenuOpen}
				width={sideMenuWidthOverride ?? sideMenuWidth}
			>
				{isValidElement(sideBarContent) ? cloneElement(sideBarContent, {...sideBarContent?.props ?? {}, setSideMenuOpen}) : undefined}
			</SideMenu>
			<HistoricNotificationDrawer
				onRead={(e) => setPageBaseContext(prev => ({...prev, notificationCount: e}))}
				slotProps={{
					backdrop: {
						style: {backgroundColor: "transparent"}
					}
				}}
				className={classes.notificationsDrawer}
				toolbarHeight={toolbarHeight}
				onClose={() => setPageBaseContext(prev => ({...prev, notificationDrawerOpen: false}))}
				open={pageBaseContext.notificationDrawerOpen}
				translations={notificationTranslations}
				notificationsCount={pageBaseContext.notificationCount}
			/>
			<Box className={classes.contentContainer} onClick={handleContentClick} ref={contentContainer}>
				<Box className={classes.content}>
					<Box className={classes.contentInner}>
						{children}
					</Box>
					{!disableFooter &&
						<Footer />
					}
				</Box>
			</Box>
			<Box className={classes.background} />
			<MobileNavBar<pages> slimLabels={slimLabels} height={mobileNavBarHeight} routesMap={routesMap} icons={tabIcons} />
		</Box>
	);
};

const CustomMenuItem = styled(MenuItem)(({theme}) => ({
	fontFamily: theme.typography.fontFamily
}));